import ToggleIconButton from '@naturehouse/design-system/components/molecules/toggle-icon-button/ToggleIconButton';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import GoogleAnalyticsEvents from '../../../common/GoogleAnalyticsEvents';
import GTM from '../../../gtm/GTM';
import GTMAddToWishlistModel from '../../../gtm/models/GTMAddToWishlistModel';
import { FeatureToggle, isFeatureEnabled } from '../../../util/featureToggle';
import GuestRegisterDialog from '../GuestRegisterDialog';
import LoginDialog, { LoginState } from '../LoginDialog';
import '../../../../css/components/_favoriteAdd.pcss';

const INSTANT_BOOKING_FEATURE = 'instant_booking';

export interface FavoriteMetaDataType extends Omit<SearchResponseHouse, 'price'> {
    favorite: boolean;
    price: SearchResponseHousePrice | [];
}

export default class AddFavoriteButton extends ToggleIconButton {
    readonly #favorited: boolean = this.hasAttribute('favorited');

    readonly #endpoint: string = this.getAttribute('endpoint') ?? '';

    public connectedCallback(): void {
        if (!this.#endpoint) {
            return;
        }

        super.connectedCallback();

        this.classList.toggle('disabled', this.#favorited);

        this.addEventListener('click', this.#handleClick.bind(this));
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('click', this.#handleClick.bind(this));
    }

    async #handleClick(): Promise<void> {
        const successCallback = async (): Promise<void> => {
            await this.#toggleFavorite();
        };

        if (document.body.classList.contains(LoginState.USER_AVAILABLE)) {
            await successCallback();
            return;
        }

        const loginDialog = LoginDialog.getInstance();
        const guestRegisterDialog = GuestRegisterDialog.getInstance();

        loginDialog.successCallback = successCallback;
        guestRegisterDialog.successCallback = successCallback;
        loginDialog.show();
    }

    async #toggleFavorite(): Promise<void> {
        this.loading = true;

        const response = await HttpClient.post(this.#endpoint);
        const favoriteResponse = response as unknown as FavoriteResponse;

        if (!favoriteResponse.success && favoriteResponse.notLoggedIn) {
            this.loading = false;
            const loginDialog = LoginDialog.getInstance();
            loginDialog.show();
            return;
        }

        if (!favoriteResponse.success) {
            return;
        }

        this.#trackUserFavorite(favoriteResponse);
        this.active = Boolean(favoriteResponse.is_favorite);
        this.loading = false;
    }

    #trackUserFavorite(response: FavoriteResponse): void {
        if (isFeatureEnabled(FeatureToggle.GOOGLE_TAG_MANAGER)) {
            if (!response.is_favorite) {
                return;
            }

            this.#trackUsingMetaData();
            return;
        }

        const houseId = response.house_id;

        if (!houseId) {
            return;
        }

        GoogleAnalyticsEvents.toggleFavourite({
            action: response.is_favorite ? 'add' : 'remove',
            houseId: houseId
        });
    }

    #trackUsingMetaData(): void {
        let metaData: Partial<FavoriteMetaDataType> = {};
        try {
            metaData = JSON.parse(this.dataset.meta ?? '{}') as Partial<FavoriteMetaDataType>;
        } catch {
            return;
        }

        if (!metaData || !metaData.id) {
            return;
        }

        GTM.trackEcommerce(
            GTMAddToWishlistModel.parseToGTMData({
                houseType: 'own',
                isDateSelected: Boolean(metaData.price && 'startDate' in metaData.price),
                id: `${metaData.id}`,
                title: metaData.title ?? '',
                country: metaData.location?.country ?? '',
                region: metaData.location?.region ?? '',
                department: metaData.location?.department ?? '',
                place: metaData.location?.place ?? '',
                pricePerNight: 0,
                instantBooking: (metaData.features || []).includes(INSTANT_BOOKING_FEATURE),
                discount: 0,
                quantity: 1
            })
        );
    }
}

if (!customElements.get('nh-add-favorite')) {
    customElements.define('nh-add-favorite', AddFavoriteButton, { extends: 'div' });
}
